import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding } from '@fortawesome/free-solid-svg-icons';
import { getRoleMeta, getStatusMeta } from 'helpers/registrationMeta';
import { getAvatarTone, getFullName, getInitials } from './registrationFormat';
import './registration.css';

/**
 * Small presentational building blocks shared by the public registration
 * screens and the admin approval queue. Colours come from `mym-tone-*`
 * classes (registration.css) so every icon, pill and avatar reads the same in
 * light and dark mode.
 */

const TONES = [
  'primary',
  'navy',
  'success',
  'warning',
  'danger',
  'info',
  'purple',
  'pink',
  'teal',
  'secondary'
];

const iconPropType = PropTypes.oneOfType([
  PropTypes.object,
  PropTypes.array,
  PropTypes.string
]);

/** Coloured icon disc. */
export const ToneIcon = ({
  icon,
  tone = 'primary',
  size = 'md',
  solid,
  className,
  spin
}) => (
  <span
    className={classNames(
      'mym-tone-icon',
      `mym-tone-${tone}`,
      {
        [`mym-tone-icon-${size}`]: size !== 'md',
        'mym-tone-icon-solid': solid
      },
      className
    )}
    aria-hidden="true"
  >
    <FontAwesomeIcon icon={icon} spin={spin} />
  </span>
);

ToneIcon.propTypes = {
  icon: iconPropType.isRequired,
  tone: PropTypes.oneOf(TONES),
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl']),
  solid: PropTypes.bool,
  className: PropTypes.string,
  spin: PropTypes.bool
};

/** Rounded label with an optional icon. */
export const TonePill = ({ tone = 'primary', icon, children, className }) => (
  <span className={classNames('mym-pill', `mym-tone-${tone}`, className)}>
    {icon && <FontAwesomeIcon icon={icon} aria-hidden="true" />}
    {children}
  </span>
);

TonePill.propTypes = {
  tone: PropTypes.oneOf(TONES),
  icon: iconPropType,
  children: PropTypes.node,
  className: PropTypes.string
};

export const RolePill = ({ roleId, className }) => {
  const meta = getRoleMeta(roleId);
  return (
    <TonePill tone={meta.tone} icon={meta.icon} className={className}>
      {meta.label}
    </TonePill>
  );
};

RolePill.propTypes = {
  roleId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  className: PropTypes.string
};

export const StatusPill = ({ status, className }) => {
  const meta = getStatusMeta(status);
  return (
    <TonePill tone={meta.tone} icon={meta.icon} className={className}>
      {meta.label}
    </TonePill>
  );
};

StatusPill.propTypes = {
  status: PropTypes.string,
  className: PropTypes.string
};

/** Initials in a colourful disc — colour is stable per person. */
export const InitialsAvatar = ({ record, size = 'md', className }) => (
  <span
    className={classNames(
      'mym-avatar',
      `mym-tone-${getAvatarTone(record?.email || getFullName(record))}`,
      { [`mym-avatar-${size}`]: size !== 'md' },
      className
    )}
    aria-hidden="true"
  >
    {getInitials(record)}
  </span>
);

InitialsAvatar.propTypes = {
  record: PropTypes.object,
  size: PropTypes.oneOf(['md', 'lg']),
  className: PropTypes.string
};

/** Facility name chips; collapses to "+N more" with a tooltip. */
// Roughly where `.mym-chip` (max-width 13rem) starts cutting a name short.
const CHIP_TRUNCATE_AT = 26;

const FacilityChip = ({ name }) => {
  const chip = (
    <span
      className="mym-chip"
      tabIndex={name.length > CHIP_TRUNCATE_AT ? 0 : undefined}
    >
      <FontAwesomeIcon icon={faBuilding} className="text-info" aria-hidden />
      <span className="mym-chip-label">{name}</span>
    </span>
  );
  if (name.length <= CHIP_TRUNCATE_AT) {
    return chip;
  }
  // Long names are cut with an ellipsis, so the full name lives in a tooltip.
  return (
    <OverlayTrigger
      placement="top"
      overlay={<Tooltip style={{ position: 'fixed' }}>{name}</Tooltip>}
    >
      {chip}
    </OverlayTrigger>
  );
};

FacilityChip.propTypes = { name: PropTypes.string.isRequired };

export const FacilityChips = ({
  facilities,
  max = 2,
  emptyText = 'None requested',
  className
}) => {
  const list = Array.isArray(facilities) ? facilities : [];
  if (list.length === 0) {
    return <span className="text-500 fs-11">{emptyText}</span>;
  }
  const visible = max ? list.slice(0, max) : list;
  const hidden = max ? list.slice(max) : [];

  return (
    <span className={classNames('d-inline-flex flex-wrap gap-1', className)}>
      {visible.map(facility => (
        <FacilityChip key={facility.id} name={String(facility.name || '')} />
      ))}
      {hidden.length > 0 && (
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip style={{ position: 'fixed' }}>
              {hidden.map(facility => facility.name).join(', ')}
            </Tooltip>
          }
        >
          <span className="mym-chip mym-chip-more" tabIndex={0}>
            +{hidden.length} more
          </span>
        </OverlayTrigger>
      )}
    </span>
  );
};

FacilityChips.propTypes = {
  facilities: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      name: PropTypes.string
    })
  ),
  max: PropTypes.number,
  emptyText: PropTypes.string,
  className: PropTypes.string
};

/** Illustration + title + text + actions, for empty / success / error states. */
export const StateMessage = ({
  art,
  title,
  children,
  actions,
  className,
  compact
}) => (
  <div
    className={classNames(
      'mym-state',
      { 'mym-state-compact': compact },
      className
    )}
  >
    {art && <div className="mym-state-art">{art}</div>}
    {title && <h4 className="mym-state-title">{title}</h4>}
    {children && <div className="mym-state-text">{children}</div>}
    {actions && <div className="mym-state-actions">{actions}</div>}
  </div>
);

StateMessage.propTypes = {
  art: PropTypes.node,
  title: PropTypes.node,
  children: PropTypes.node,
  actions: PropTypes.node,
  className: PropTypes.string,
  compact: PropTypes.bool
};
